import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";

type Text = { sv: string; en: string };
export type ConsentCookie = { name: string; class: string; purpose: Text; lifetime: Text; storage?: string };
/** ``GET /api/auth/me/privacy``: the user's choices, and for the chat the
 *  texts its consent notice shows (the active org's provider and region). */
export type PrivacyChoices = {
  support_chat_cookies: { granted: boolean; at: string | null; valid: boolean };
  onboarding_tracking: { granted: boolean; at: string | null };
  marketing: { granted: boolean; at: string | null };
  support_chat_available: boolean;
  evaluation?: boolean;
  region?: string | null;
  /** Where the active org's variant stores the data (sv/en). */
  location?: Text | null;
  consent?: { notice: Text; cookies: ConsentCookie[]; privacy_addon: Text; hash: string };
};
export type Purpose = "support_chat_cookies" | "onboarding_tracking" | "marketing";

export const privacyKey = ["auth", "me", "privacy"] as const;

export function usePrivacyChoices(enabled = true) {
  return useQuery({
    queryKey: privacyKey,
    queryFn: async () => (await api.get<PrivacyChoices>("/auth/me/privacy")).data,
    enabled,
  });
}

export type SetPrivacyChoiceVars = {
  purpose: Purpose;
  granted: boolean;
  text_hash: string;
  ui_language: string;
  source?: "account_privacy" | "chat_notice";
};

export function useSetPrivacyChoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: SetPrivacyChoiceVars) =>
      (
        await api.put(`/auth/me/privacy/${v.purpose}`, {
          granted: v.granted,
          text_hash: v.text_hash,
          ui_language: v.ui_language,
          source: v.source ?? "account_privacy",
        })
      ).data,
    onSuccess: (_data, v) => {
      void qc.invalidateQueries({ queryKey: privacyKey });
      void qc.invalidateQueries({ queryKey: keys.me });
      if (v.purpose === "support_chat_cookies" && !v.granted) {
        // The consent is withdrawn: end the chat and remove its cookies now.
        void import("@/features/support/useIntercomMessenger").then((m) => m.shutdownIntercom());
      }
    },
  });
}
